<template>
    <DialogBase class="dialog-projection">

        <template #title>
            Manual Projections
        </template>

        <template #content>

            <div v-if="isAdministrator" class="dialog-projection__mode">
                <v-btn-toggle 
                    mandatory 
                    class="toggle" 
                    color="color1" 
                    elevation="0" 
                    density="default"
                    v-model="mode">
                    <v-btn 
                        class="toggle-button" 
                        elevation="0"
                        :value="keyModeSimple"> 
                        <span>Simple</span>
                    </v-btn>
                    <v-btn 
                        class="toggle-button" 
                        elevation="0"
                        :value="keyModeAdvanced"> 
                        <span>Advanced</span>
                    </v-btn>
                </v-btn-toggle>
            </div>

            <div class="dialog-projection__content">
                <div class="dialog-projection__scroll">
                    <div v-if="hasProjections" class="dialog-projection__cards">
                        <div
                            v-for="(projection, index) in projections"
                            :key="projection.id"
                            class="dialog-projection__card">
                            <div class="dialog-projection__card-header">
                                <div class="dialog-projection__card-header-title">
                                    Perspective {{ index + 1 }}
                                </div>
                                <div class="dialog-projection__card-header-delete">
                                    <v-btn 
                                        class="button-remove"
                                        variant="plain" 
                                        elevation="0"
                                        @click="handleRemoveProjection(index)">
                                        <v-icon icon="fa-solid fa-trash"></v-icon>
                                    </v-btn>
                                </div>
                            </div>
                            <div v-if="isModeSimple" class="dialog-projection__card-row">
                                <div class="dialog-projection__card-row-name">
                                    Perspective
                                </div>
                                <div class="dialog-projection__card-row-value">
                                    <v-select
                                        hide-details 
                                        control-variant="stacked"
                                        :model-value="projection.perspective"
                                        :items="perspectives"
                                        @update:modelValue="handlePerspectiveChange(projection, $event)">
                                    </v-select>
                                </div>
                            </div>

                            <div v-if="isModeAdvanced" class="dialog-projection__card-row">
                                <div class="dialog-projection__card-row-name">
                                    Orientation
                                </div>
                                <div class="dialog-projection__card-row-value">
                                    <v-number-input
                                        v-model="projection.view.azimuth"
                                        hide-details
                                        control-variant="stacked"
                                        label="azimuth">
                                    </v-number-input>
                                    <v-number-input
                                        v-model="projection.view.elevation"
                                        hide-details
                                        control-variant="stacked"
                                        label="elevation">
                                    </v-number-input>
                                    <v-number-input
                                        v-model="projection.view.roll"
                                        hide-details
                                        control-variant="stacked"
                                        label="roll">
                                    </v-number-input>
                                </div>
                            </div>

                            <div v-if="isModeAdvanced" class="dialog-projection__card-row">
                                <div class="dialog-projection__card-row-name">
                                    Center
                                </div>
                                <div class="dialog-projection__card-row-value">
                                    <v-number-input
                                        v-model="projection.view.center_x"
                                        hide-details
                                        control-variant="stacked"
                                        label="x">
                                    </v-number-input>
                                    <v-number-input
                                        v-model="projection.view.center_y"
                                        hide-details
                                        control-variant="stacked"
                                        label="y">
                                    </v-number-input>
                                    <v-number-input
                                        v-model="projection.view.center_z"
                                        hide-details
                                        control-variant="stacked"
                                        label="z">
                                    </v-number-input>
                                </div>
                            </div>

                            <div v-if="isModeAdvanced" class="dialog-projection__card-row">
                                <div class="dialog-projection__card-row-name">
                                    <!-- Others -->
                                </div>
                                <div class="dialog-projection__card-row-value">
                                    <!--<v-number-input
                                        v-model="projection.view.distance"
                                        hide-details
                                        control-variant="stacked"
                                        label="distance">
                                    </v-number-input>
                                    <v-number-input
                                        v-model="projection.view.ortho_scale"
                                        hide-details
                                        control-variant="stacked"
                                        label="ortho scale">
                                    </v-number-input>-->
                                    <v-number-input
                                        v-model="projection.view.weight"
                                        hide-details
                                        control-variant="stacked"
                                        label="weight">
                                    </v-number-input>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="dialog-projection__empty">
                        No perspectives added yet.
                    </div>
                </div>

                <v-btn 
                    class="dialog-projection__button-add"
                    variant="tonal" 
                    elevation="0"
                    color="color1" 
                    style="opacity:0.76;"
                    @click="handleAddProjection">
                    <v-icon icon="fa-solid fa-plus"></v-icon>
                    Add Perspective
                </v-btn>

            </div>

            <div v-if="isAdministrator" class="dialog-projection__extra">
                <v-btn-toggle 
                    mandatory 
                    class="toggle" 
                    color="color1" 
                    elevation="0" 
                    density="default"
                    v-model="card.custom.variant">
                    <v-btn 
                        class="toggle-button" 
                        elevation="0"
                        :value="keyVariantQwen"> 
                        <span>Qwen</span>
                    </v-btn>
                    <v-btn 
                        class="toggle-button" 
                        elevation="0" 
                        :disabled="true"
                        :value="keyVariantFlux"> 
                        <span>Flux</span>
                    </v-btn>
                    <v-btn 
                        class="toggle-button" 
                        elevation="0"
                        :value="keyVariantOpenAI"> 
                        <span>OpenAI</span>
                    </v-btn>
                </v-btn-toggle>

            </div>

            <div v-if="isAdministrator" class="dialog-projection__extra">
                <v-switch 
                    hide-details 
                    color="color1" 
                    label="Sequence" 
                    v-model="card.custom.sequence">
                </v-switch>
                <v-switch 
                    hide-details 
                    color="color1" 
                    label="Delight" 
                    v-model="card.custom.delight">
                </v-switch>
                <v-switch 
                    hide-details 
                    color="color1" 
                    label="Faceswap" 
                    v-model="card.custom.faceswap">
                </v-switch>
                <v-switch 
                    hide-details 
                    color="color1" 
                    label="Refloor" 
                    v-model="card.custom.refloor">
                </v-switch>
            </div>
        </template>

        <template #actions>
            <v-btn 
                variant="elevated" 
                elevation="0" 
                color="surface" 
                style="opacity:0.76;"
                @click="handleCancel">
                Cancel
            </v-btn>
            <v-btn 
                variant="elevated" 
                elevation="0" 
                color="color1" 
                style="opacity:0.76;" 
                :disabled="!hasProjections"
                @click="handleSave">
                Save
            </v-btn>
        </template>

    </DialogBase>
</template>

<script>
import { mapGetters } from '@generio-ai/library-legacy/plugins/vuex'
import { View } from '@generio-ai/library-legacy/stores/_api'
import { mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import { DialogBase } from '@generio-ai/library-legacy/components/dialogs'

const SIMPLE_VIEW_PRESETS = Object.freeze({
    front:  { azimuth: 0, elevation: 0 },
    back:   { azimuth: 180, elevation: 0 },
    left:   { azimuth: 90, elevation: 0 },
    right:  { azimuth: -90, elevation: 0 },
    top:    { azimuth: 0, elevation: 90 },
    bottom: { azimuth: 0, elevation: -90 }
})

export default {
    name: 'DialogProjection',
    components: {
        DialogBase
    },
    props: {
        card: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            mode: 'simple',
            projections: [],
            nextProjectionId: 1
        }
    },
    computed: {
        ...mapGetters('auth', [
            'isAdministrator'
        ]),
        keyModeSimple() {
            return 'simple'
        },
        keyModeAdvanced() {
            return 'advanced'
        },
        keyVariantQwen() {
            return 'qwen'
        },
        keyVariantFlux() {
            return 'flux'
        },
        keyVariantOpenAI() {
            return 'openai'
        },
        isModeSimple() {
            return this.mode === this.keyModeSimple
        },
        isModeAdvanced() {
            return this.mode === this.keyModeAdvanced
        },
        hasProjections() {
            return this.projections.length > 0
        },
        perspectives() {
            return Object.keys(SIMPLE_VIEW_PRESETS)
        }
    },
    watch: {
        mode(newMode, oldMode) {
            if (newMode === oldMode) return
            if (newMode !== this.keyModeSimple) return

            this.projections.forEach(projection => {
                const perspective = this.getSimplePerspectiveForView(projection.view)
                projection.perspective = perspective
                this.applySimplePerspective(projection.view, perspective)
            })
        }
    },
    methods: {
        ...mapActions('dialogs', [
            'close'
        ]),
        normalizeNumber(value, fallback = 0) {
            const numericValue = Number(value)
            return Number.isFinite(numericValue) ? numericValue : fallback
        },
        normalizeNullableNumber(value) {
            if (value === null || value === undefined || value === '') return null

            const numericValue = Number(value)
            return Number.isFinite(numericValue) ? numericValue : null
        },
        normalizeAngle(value) {
            let normalized = this.normalizeNumber(value)

            while (normalized > 180) normalized -= 360
            while (normalized <= -180) normalized += 360

            return normalized
        },
        createView(view = {}) {
            return new View(
                this.normalizeAngle(view?.azimuth),
                this.normalizeNumber(view?.elevation),
                this.normalizeNumber(view?.roll),
                this.normalizeNumber(view?.weight, 1),
                this.normalizeNullableNumber(view?.distance),
                this.normalizeNullableNumber(view?.ortho_scale),
                this.normalizeNullableNumber(view?.center_x),
                this.normalizeNullableNumber(view?.center_y),
                this.normalizeNullableNumber(view?.center_z)
            )
        },
        applySimplePerspective(view, perspective) {
            const preset = SIMPLE_VIEW_PRESETS[perspective] ?? SIMPLE_VIEW_PRESETS.front

            view.azimuth = preset.azimuth
            view.elevation = preset.elevation
            view.roll = 0
            view.weight = 1
            view.distance = null
            view.ortho_scale = null
            view.center_x = null
            view.center_y = null
            view.center_z = null

            return view
        },
        createProjection(perspective = 'front') {
            const view = this.createView()
            this.applySimplePerspective(view, perspective)

            return {
                id: this.nextProjectionId++,
                perspective,
                view
            }
        },
        normalizeProjection(view = {}) {
            const normalizedView = this.createView(view)

            return {
                id: this.nextProjectionId++,
                perspective: this.getSimplePerspectiveForView(normalizedView),
                view: normalizedView
            }
        },
        getSimplePerspectiveForView(view) {
            const currentAzimuth = this.normalizeAngle(view?.azimuth)
            const currentElevation = this.normalizeNumber(view?.elevation)

            return Object.entries(SIMPLE_VIEW_PRESETS)
                .map(([key, preset]) => ({
                    key,
                    score:
                        Math.abs(this.normalizeAngle(currentAzimuth - preset.azimuth)) +
                        Math.abs(currentElevation - preset.elevation)
                }))
                .sort((a, b) => a.score - b.score)[0]?.key ?? 'front'
        },
        isSimpleView(view) {
            const perspective = this.getSimplePerspectiveForView(view)
            const preset = SIMPLE_VIEW_PRESETS[perspective]

            return this.normalizeAngle(view?.azimuth) === preset.azimuth
                && this.normalizeNumber(view?.elevation) === preset.elevation
                && this.normalizeNumber(view?.roll) === 0
                && this.normalizeNumber(view?.weight, 1) === 1
                && this.normalizeNullableNumber(view?.distance) === null
                && this.normalizeNullableNumber(view?.ortho_scale) === null
                && this.normalizeNullableNumber(view?.center_x) === null
                && this.normalizeNullableNumber(view?.center_y) === null
                && this.normalizeNullableNumber(view?.center_z) === null
        },
        inferInitialMode(projections) {
            return projections.every(projection => this.isSimpleView(projection.view))
                ? this.keyModeSimple
                : this.keyModeAdvanced
        },
        getInitialProjections() {
            const storedViews = this.card?.custom?.views

            if (Array.isArray(storedViews) && storedViews.length > 0) {
                return storedViews.map(view => this.normalizeProjection(view))
            }

            return [this.createProjection()]
        },
        getNextSimplePerspective() {
            const usedPerspectives = new Set(
                this.projections.map(projection => projection.perspective)
            )

            return this.perspectives.find(perspective => !usedPerspectives.has(perspective))
                ?? this.perspectives[0]
        },
        handleAddProjection() {
            const perspective = this.isModeSimple
                ? this.getNextSimplePerspective()
                : 'front'

            this.projections = [
                ...this.projections,
                this.createProjection(perspective)
            ]
        },
        handleRemoveProjection(index) {
            if (index < 0 || index >= this.projections.length) return

            this.projections = this.projections.filter((projection, projectionIndex) =>
                projectionIndex !== index
            )
        },
        handlePerspectiveChange(projection, perspective) {
            projection.perspective = this.perspectives.includes(perspective)
                ? perspective
                : 'front'

            this.applySimplePerspective(projection.view, projection.perspective)
        },
        handleSave() {
            this.card.custom ??= {}
            this.card.custom.projections = 'custom'
            this.card.custom.views = this.projections.map(({ view }) => this.createView(view))

            this.close()
        },
        handleCancel() {
            this.close()
        }
    },
    mounted() {
        this.projections = this.getInitialProjections()
        this.mode = this.inferInitialMode(this.projections)
    }
}
</script>

<style>
.dialog-projection__sequence .v-label {
    color: rgb(var(--v-theme-secondary)) !important;
    font-size: 1.0rem !important;
}
</style>

<style scoped>
.dialog-projection__mode {
    margin-bottom: 10px;
}

:is(.dialog-projection__mode, .dialog-projection__extra) .toggle {
    display: flex;
    width: 100%;
}

:is(.dialog-projection__mode, .dialog-projection__extra) .toggle-button {
    flex: 1;
}

:is(.dialog-projection__mode, .dialog-projection__extra) .toggle {
    max-height: 40px !important;
    min-height: 40px !important;
	border: 1px solid rgb(var(--v-theme-lightbase));
    border-radius: 8px !important;
}

:is(.dialog-projection__mode, .dialog-projection__extra) .toggle-button {
    max-height: 40px !important;
    min-height: 40px !important;
	background-color: rgb(var(--v-theme-darkbase));
    border-radius: 0px !important;
}

:is(.dialog-projection__mode, .dialog-projection__extra) .toggle-button:not(:first-child) {
    border-left: 1px solid rgb(var(--v-theme-lightbase));
}

.dialog-projection__content {
    width: 100%;
    height: 400px;
    min-height: 400px;
    padding: 10px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow: hidden;
    background-color: rgb(var(--v-theme-lightbase));
}

.dialog-projection__scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-right: 4px;
}

.dialog-projection__cards {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.dialog-projection__card {
    padding: 10px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background-color: rgb(var(--v-theme-darkbase));
    border: 2px solid rgb(var(--v-theme-color1));
}

.dialog-projection__empty {
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed rgb(var(--v-theme-shadow2));
    border-radius: 8px;
    color: rgb(var(--v-theme-secondary));
    background-color: rgba(var(--v-theme-darkbase), 0.35);
}

.dialog-projection__card-header {
    width: 100%;
    padding-bottom: 3px;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid rgb(var(--v-theme-shadow2));
}

.dialog-projection__card-header-title {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 15px;
    padding-right: 15px;
    font-size: 1.0rem;
    font-weight: 500;
    color: rgb(var(--v-theme-primary));
    border-radius: 8px;
    background-color: rgba(var(--v-theme-lightbase), 0.5);
}

.dialog-projection__card-header-delete {
}

.dialog-projection__card-header-delete .button-remove:hover {
    background-color: rgba(var(--v-theme-error), 0.5);
}

.dialog-projection__card-row {
    width: 100%;
    display: flex;
    flex-direction: row;
}

.dialog-projection__card-row-name {
    margin-right: 10px;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    font-size: 1rem;
    color: rgb(var(--v-theme-secondary));
}

.dialog-projection__card-row-value {
    flex: 3;
    display: flex;
    flex-direction: row;
    gap: 10px;
}

.dialog-projection__card-row-value > * {
    flex: 1;
    min-width: 0;
}

.dialog-projection__button-add {
    width: 100%;
    align-self: stretch;
}

.dialog-projection__extra {
    margin-top: 10px;
    display: flex;
    justify-content: center;
    gap: 20px;
    border-radius: 8px;
    background-color: rgba(var(--v-theme-lightbase), 0.5);
}

@media (max-width: 700px) {
    .dialog-projection__card-row {
        flex-direction: column;
        gap: 8px;
    }

    .dialog-projection__card-row-name {
        margin-right: 0;
        justify-content: flex-start;
    }
}

@media (max-width: 600px) {
    .dialog-projection__card-row-value {
        flex-direction: column;
    }
}
</style>
